export const isKitChildItem = (item, unScannedItems) => {
  if (!item || !unScannedItems?.length) return false;
  const parent = unScannedItems[0];
  return parent?.product_type === 'individual' &&
    parent?.child_items?.some(c => c.product_id === item.product_id);
};

const isKitUpdateNeeded = (parentItem, numberOfKits) => {
  let updateKit = false;
  let updateKitFlag = true;

  if (parentItem.scanned_qty > 0) {
    parentItem.child_items.forEach(item => {
      const eachKitConsistOn = (item.scanned_qty + item.qty_remaining) / numberOfKits;
      const scannedQty = item.scanned_qty > parentItem.scanned_qty * eachKitConsistOn;
      const isKitScanned = item.scanned_qty % eachKitConsistOn;
      if (!isKitScanned && scannedQty && updateKitFlag) {
        updateKit = true;
      } else {
        const numberOfItems = parentItem.scanned_qty + 1;
        if (item.scanned_qty > eachKitConsistOn * numberOfItems) {
          updateKit = true;
        } else {
          updateKit = false;
          updateKitFlag = false;
        }
      }
    });
  } else {
    parentItem.child_items.forEach(item => {
      const eachKitConsistOn = (item.qty_remaining + item.scanned_qty) / numberOfKits;
      const isKitScanned = item.scanned_qty % eachKitConsistOn;
      if (isKitScanned < 1 && item.scanned_qty > parentItem.scanned_qty) {
        updateKit = true;
      } else {
        if (item.scanned_qty > eachKitConsistOn) {
          updateKit = true;
        } else {
          updateKit = false;
        }
      }
    });
  }

  return updateKit;
};

export const checkKitRoundComplete = (unScannedItems, scannedItems, originalUnScannedItems = null) => {
  if (originalUnScannedItems) {
    const originalParent = originalUnScannedItems[0];
    if (originalParent?._kitRoundChildTemplate && originalParent.total_qty > 1) {
      const parentGone = !unScannedItems.some(
        item => item.order_item_id === originalParent.order_item_id && item.product_id === originalParent.product_id
      );
      if (parentGone) {
        const roundsCompleted = (originalParent.scanned_qty || 0) + 1;
        if (roundsCompleted < originalParent.total_qty) {
          return { type: 'depends_round', parentItem: originalParent, roundsCompleted };
        }
        return { type: 'depends_complete', parentItem: originalParent, roundsCompleted };
      }
    }

    // The last round's scan drops the kit from the unscanned list, so it can't be read below.
    if (originalParent?.child_items?.length && !originalParent._kitRoundChildTemplate &&
      originalParent.product_type !== 'depends' && originalParent.total_qty > 1) {
      const parentGone = !unScannedItems.some(
        item => item.order_item_id === originalParent.order_item_id && item.product_id === originalParent.product_id
      );
      if (parentGone) {
        return { type: 'kit_complete', parentItem: originalParent };
      }
    }
  }

  const firstItem = unScannedItems[0];
  if (!firstItem?.child_items?.length || firstItem.product_type === 'depends') {
    return null;
  }

  const numberOfKits = firstItem.total_qty;
  if (!numberOfKits || numberOfKits <= 1) return null;

  const firstChild = firstItem.child_items[0];
  const eachKitConsistOn = (firstChild.qty_remaining + firstChild.scanned_qty) / numberOfKits;
  if (!eachKitConsistOn || firstChild.scanned_qty === 0) return null;

  const isRoundComplete = firstChild.scanned_qty % eachKitConsistOn === 0;
  if (!isRoundComplete) return null;

  const childItemsScanned = scannedItems.some(
    item => item.order_item_id === firstItem.order_item_id && item.child_items?.length > 1
  );

  if (firstItem.child_items.length === 1 && !childItemsScanned) {
    return { type: 'single_child', eachKitConsistOn, parentItem: firstItem };
  }

  if (isKitUpdateNeeded(firstItem, numberOfKits)) {
    return { type: 'multi_child', parentItem: firstItem };
  }

  return null;
};

export const applyKitParentQtyUpdate = (unScannedItems, scannedItems, roundResult) => {
  if (!roundResult) return { unScannedItems, scannedItems, showKitPopup: false };

  const { type, eachKitConsistOn } = roundResult;
  const updatedUnscanned = [...unScannedItems];
  const updatedScanned = [...scannedItems];

  if (type === 'depends_round') {
    const { parentItem, roundsCompleted } = roundResult;
    const resetParent = {
      ...parentItem,
      scanned_qty: roundsCompleted,
      qty_remaining: parentItem.total_qty - roundsCompleted,
      child_items: parentItem._kitRoundChildTemplate.map(c => {
        const scannedChild = updatedScanned.find(s => s.sku === c.sku && s.order_item_id === c.order_item_id);
        return { ...c, scanned_qty: scannedChild?.scanned_qty || 0 };
      })
    };
    updatedUnscanned.unshift(resetParent);
    const parentIdx = updatedScanned.findIndex(item => item.name === resetParent.name);
    if (parentIdx !== -1) {
      updatedScanned[parentIdx] = resetParent;
    } else {
      updatedScanned.unshift(resetParent);
    }
    return {
      unScannedItems: updatedUnscanned,
      scannedItems: updatedScanned,
      showKitPopup: !!(resetParent.confirmation && resetParent.instruction?.length > 0),
      kitInstructions: resetParent.instruction || ''
    };
  }

  if (type === 'depends_complete') {
    const { parentItem, roundsCompleted } = roundResult;
    const completedParent = {
      ...parentItem,
      scanned_qty: roundsCompleted,
      qty_remaining: 0,
      child_items: []
    };
    const parentIdx = updatedScanned.findIndex(item => item.name === completedParent.name);
    if (parentIdx !== -1) {
      updatedScanned[parentIdx] = completedParent;
    } else {
      updatedScanned.unshift(completedParent);
    }
    return { unScannedItems: updatedUnscanned, scannedItems: updatedScanned, showKitPopup: false };
  }

  if (type === 'kit_complete') {
    const { parentItem } = roundResult;
    const completedParent = { ...parentItem, scanned_qty: parentItem.total_qty, qty_remaining: 0 };
    const parentIdx = updatedScanned.findIndex(item => item.name === completedParent.name);
    if (parentIdx !== -1) {
      updatedScanned[parentIdx] = completedParent;
    } else {
      updatedScanned.unshift(completedParent);
    }
    return { unScannedItems: updatedUnscanned, scannedItems: updatedScanned, showKitPopup: false };
  }

  const parent = { ...updatedUnscanned[0] };

  if (type === 'single_child') {
    const scannedQty = Math.floor(parent.child_items[0].scanned_qty / eachKitConsistOn);
    parent.scanned_qty = scannedQty;
    parent.qty_remaining = parent.total_qty - scannedQty;
  } else {
    parent.scanned_qty = (parent.scanned_qty || 0) + 1;
    parent.qty_remaining = (parent.qty_remaining || 0) - 1;
  }

  updatedUnscanned[0] = parent;

  const parentIdx = updatedScanned.findIndex(item => item.name === parent.name);
  if (parentIdx !== -1) {
    updatedScanned[parentIdx] = parent;
  } else {
    updatedScanned.unshift(parent);
  }

  return {
    unScannedItems: updatedUnscanned,
    scannedItems: updatedScanned,
    showKitPopup: !!(parent.confirmation && parent.instruction?.length > 0),
    kitInstructions: parent.instruction || ''
  };
};
